
import _ from 'lodash';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { setObjectFit } from '../../base/conference';
import { isToolbarButtonEnabled } from '../../base/config/functions.web';
import { openDialog } from '../../base/dialog';
import { isDeviceLandScape } from '../../base/environment/environment';
import {
    IconChat,
    IconCloseCircle,
    IconContain,
    IconCover,
    IconCrown,
    IconMicDisabled,
    IconMicrophone,
    IconModeratorPin,
    IconMuteEveryoneElse,
    IconMuteVideoEveryoneElse,
    IconRaisedHandSlash,
    IconShareDesktop,
    IconUserGroup,
    IconUserPinned,
    IconVideoOff,
    IconVolume
} from '../../base/icons';
import {
    PARTICIPANT_ROLE,
    SET_MODERATOR_PIN,
    getLocalParticipant,
    grantPanelist,
    grantPresenter,
    isLocalParticipantModerator,
    isParticipantModerator,
    isParticipantPanelist,
    isParticipantPresenter,
    participantVolumeChanged,
    pinParticipant,
    revokePanelist,
    revokePresenter,
    setWebinarHidden
} from '../../base/participants';
import { PERMISSIONS, hasPermission } from '../../base/permissions';
import { getBrandingColorPalette } from '../../base/styles';
import {
    getIsParticipantAudioMuted,
    getIsParticipantVideoMuted
} from '../../base/tracks';
import SendToRoomButton from '../../breakout-rooms/components/web/SendToRoomButton';
import { PARTICIPANT_MENU_BUTTONS as BUTTONS } from '../../breakout-rooms/constants';
import {
    getBreakoutRooms,
    getCurrentRoomId,
    isInBreakoutRoom
} from '../../breakout-rooms/functions';
import { openChat } from '../../chat/actions';
import { setIsPollsTabFocused } from '../../chat/actions.any';
import { pollsTabFocusedHandler } from '../../conference/actions.web';
import { setLargeVideoFullscreen } from '../../large-video/actions.any';
import { NOTIFICATION_TYPE, showNotification } from '../../notifications';
import { sendVoiceModeCommand } from '../../subtitles/actions';
import {
    ForceMuteRemoteDialog,
    ForceMuteRemoteParticipantVideoDialog,
    GrantModeratorDialog,
    KickRemoteParticipantDialog,
    MuteEveryoneDialog,
    MuteEveryonesVideoDialog
} from '../../video-menu';
import { VolumeControlDialog } from '../../video-menu/components/web';
import DemoteToVisitorDialog from '../../video-menu/components/web/DemoteToVisitorDialog';
import MuteRemoteParticipantsVideoDialog from '../../video-menu/components/web/MuteRemoteParticipantsVideoDialog';
import {
    iAmVisitor,
    isVisitorModeSupported
} from '../../visitors/functions';
import {
    amIInBackstage,
    bringLive,
    isBackstageModeEnabled,
    isHiddenMeEnabled,
    sendToBackstage
} from '../../webinar';
import { close, closeParticipantsPaneModal } from '../actions';
import { clearRaisedHandByParticipant } from '../functions';

import { ParticipantContextMenu } from './ParticipantContextMenu';
import {
    ContextMenuIcon,
    ContextMenuItem,
    ContextMenuItemGroup
} from './styled';


const _inlineStyles = {
    _s0: { padding: '10px 16px' }
};

declare var config: object;

type Props = {

  /**
   * Callback used to open a confirmation dialog for audio muting.
   */
  muteAudio: Function,

  /**
   * Target elements against which positioning calculations are made.
   */
  offsetTarget: HTMLElement,

  /**
   * Callback for the mouse entering the component.
   */
  onEnter: Function,

  /**
   * Callback for the mouse leaving the component.
   */
  onLeave: Function,

  /**
   * Callback for making a selection in the menu.
   */
  onSelect: Function,

  /**
   * Participant reference.
   */
  participant: Object,
};

export const MeetingParticipantContextMenu = ({
    offsetTarget,
    onEnter,
    onLeave,
    onSelect,
    muteAudio,
    participant: openedFor
}: Props) => {
    const dispatch = useDispatch();
    const participants = useSelector(state => state['features/base/participants']);

    // The pane hands us the participant object as it looked when the menu was
    // opened, and redux replaces that object on every update. Rendering from the
    // snapshot is why the items whose label depends on current state -- Hide
    // from others / Show to others, the panelist and presenter toggles -- kept
    // offering the action that had just been carried out. Read the live entry
    // and keep the snapshot only as a fallback, so a participant who leaves
    // mid-menu still renders for the frame before it closes.
    const participant = participants.find(p => p.id === openedFor?.id) ?? openedFor;
    const isLocalModerator = useSelector(isLocalParticipantModerator);
    const canGrantRoles = useSelector(state => hasPermission(state, PERMISSIONS.GRANT_ROLES));
    const inBackstage = useSelector(amIInBackstage);

    // Backstage and hide-me are meeting features (ENABLE_BACKSTAGE_MODE /
    // ENABLE_HIDDEN_ME in the token). Backstage belongs to the main room, so
    // it is not offered from inside a breakout room either. A participant who
    // is hidden right now can always be shown again.
    const inBreakoutRoom = useSelector(isInBreakoutRoom);
    const backstageEnabled = useSelector(isBackstageModeEnabled) && !inBreakoutRoom;
    const hiddenMeEnabled = useSelector(isHiddenMeEnabled);

    const totalModerators = participants.filter(it => it.role === 'moderator' && !it?.isFakeParticipant);
    const isMainModerator = useSelector(state => state['features/base/jwt']?.user?.moderator || state['features/base/jwt']?.moderator);
    const isPrivateChatDisabled = useSelector(state => state['features/chat']?.isPrivateChatDisabled);
    const moderators_allowed = useSelector(state => state['features/base/mtApi']?.pregData?.moderators_allowed);
    const { objectFit } = useSelector(state => state['features/base/conference']);
    const { largevideofullscreen } = useSelector(state => state['features/large-video']);
    const lockaudio = useSelector(
    state => state['features/base/conference']?.lockAudio
    );
    const visibleButtons = useSelector(state => state['features/base/config'].interfaceConfig?.TOOLBAR_BUTTONS);
    const interfaceConfig = useSelector(state => state['features/base/config'].interfaceConfig);
    const _visibleButtons = new Set(visibleButtons);
    const lockvideo = useSelector(
    state => state['features/base/conference']?.lockVideo
    );
    const isChatButtonEnabled = useSelector(isToolbarButtonEnabled('chat'));
    const isParticipantVideoMuted = useSelector(
    getIsParticipantVideoMuted(participant)
    );
    const visitorsMode = useSelector(state => iAmVisitor(state));
    const visitorsSupported = useSelector(isVisitorModeSupported);
    const isParticipantAudioMuted = useSelector(
    getIsParticipantAudioMuted(participant)
    );
    const { jwt = null } = useSelector(state => state['features/base/jwt']);
    const { t } = useTranslation();
    const isPersonModerator = isParticipantModerator(participant);
    const rooms = useSelector(getBreakoutRooms);
    const conference = useSelector(state => state['features/base/conference'].conference);
    const state1 = useSelector(state => state);
    const voiceModeState = useSelector(
        state => state['features/subtitles']?._voiceModeByParticipant?.get?.(participant?.id));
    const voiceCapability = useSelector(
        state => state['features/subtitles']?._voiceCapabilities?.get?.(participant?.id));
    const voiceCatalog = useSelector(
        state => state['features/subtitles']?._voiceCatalogByParticipant?.get?.(participant?.id));
    const voiceModeEnabled = useSelector(
        state => state['features/base/config']?.transcription?.voiceMode?.enabled !== false);
    const cloneStatus = voiceCapability?.cloned_voice_status
        || voiceModeState?.cloned_voice_status
        || 'pending';
    const cloneStatusLabel = cloneStatus === 'ready'
        ? t('transcribing.cloneStatusReady')
        : cloneStatus === 'processing'
            ? t('transcribing.cloneStatusProcessing')
            : t('transcribing.cloneStatusFallback');
    const cloneVoiceId = voiceCatalog?.supportedVoices
        ?.find?.(voice => voice?.cloneRequired)?.id;
    const menuStyle = { color: interfaceConfig?.applyMeetingSettings ? `${getBrandingColorPalette(interfaceConfig)?.primary} !important` : '' };

    const grantModerator = useCallback(() => {

        if (_.isNil(jwt)) {
            return;
        }
        if (participant.role === PARTICIPANT_ROLE.MODERATOR && totalModerators.length >= moderators_allowed) {
            dispatch(
            openDialog(GrantModeratorDialog, {
                participantID: participant.id
            })
            );

            return;
        } else if (totalModerators.length >= moderators_allowed) {
            dispatch(showNotification({
                descriptionArguments: { number: moderators_allowed },
                descriptionKey: 'notify.maxGrantModeratorDes',
                titleKey: 'notify.maxGrantModerator',
                customActionNameKey: 'notify.upgradeNow',
                customActionHandler: () => window.open(`${config?.URL_PREFIX}updateSubscription`, '_new')
            }));

            return;
        }

        dispatch(
            openDialog(GrantModeratorDialog, {
                participantID: participant.id
            })
        );
    }, [ dispatch, participant ]);

    const togglePanelist = useCallback(() => {
        dispatch(isParticipantPanelist(participant)
            ? revokePanelist(participant.id)
            : grantPanelist(participant.id));
    }, [ dispatch, participant ]);

    const togglePresenter = useCallback(() => {
        dispatch(isParticipantPresenter(participant)
            ? revokePresenter(participant.id)
            : grantPresenter(participant.id));
    }, [ dispatch, participant ]);

    const toggleHidden = useCallback(() => {
        dispatch(setWebinarHidden(participant.id, participant?.webinarHidden !== true));
    }, [ dispatch, participant ]);

    // Ask Prosody to switch this stage occupant's room. Backstage is separate,
    // so each request can only target people in the current room.
    const moveStageOccupant = useCallback(() => {
        dispatch(inBackstage
            ? bringLive(participant.id)
            : sendToBackstage(participant.id));
    }, [ dispatch, inBackstage, participant ]);

    const kick = useCallback(() => {
        dispatch(
      openDialog(KickRemoteParticipantDialog, {
          participantID: participant.id
      })
        );
    }, [ dispatch, participant ]);

    const muteEveryoneElse = useCallback(() => {
        dispatch(
      openDialog(MuteEveryoneDialog, {
          exclude: [ participant.id ]
      })
        );
    }, [ dispatch, participant ]);

    const muteEveryoneElseVideo = useCallback(() => {
        dispatch(
            openDialog(MuteEveryonesVideoDialog, {
                exclude: [ participant.id ]
            })
        );
    }, [ dispatch, participant ]);

    const muteVideo = useCallback(() => {
        dispatch(
      openDialog(MuteRemoteParticipantsVideoDialog, {
          participantID: participant.id
      })
        );
    }, [ dispatch, participant ]);

    const participantModeratorPin = useCallback(() => {
        if (!conference) {

            return;
        }

        const myId = conference.myUserId();
        const isModeratorPinned = participant && participant.moderatorPin;
        const pinAction = isModeratorPinned ? 'unpin' : 'pin';

        conference.sendMessage(
            {
                type: SET_MODERATOR_PIN,
                participant_id: participant.id,
                senderId: myId,
                action: pinAction
            }
        );


        // Show notification
        const notificationKey = pinAction === 'pin'
            ? t('videothumbnail.pinByModerator')
            : t('videothumbnail.unpinByModerator');

        const notificationDescriptionKey = pinAction === 'pin'
            ? t('videothumbnail.moderatorPinDiscription')
            : t('videothumbnail.unpinByModerator');

        onLeave();

        dispatch(
                showNotification(
                    {
                        appearance: NOTIFICATION_TYPE.NORMAL,
                        titleKey: notificationKey,
                        descriptionKey: notificationDescriptionKey
                    },
                    3000
                )
        );
    }, [ dispatch, participant, conference, onLeave ]);

    const handleobjectfit = useCallback(() => {

        dispatch(setObjectFit(
            objectFit === 'cover' ? 'contain' : 'cover'
        ));
        onLeave();
    }, [ dispatch, participant, onLeave ]);

    const forceMuteVideo = useCallback(() => {
        dispatch(
      openDialog(ForceMuteRemoteParticipantVideoDialog, {
          participantID: participant.id
      })
        );
    }, [ dispatch, participant ]);

    const volumeControl = useCallback(() => {
        dispatch(openDialog(VolumeControlDialog, {
            participantID: participant.id,
            initialVolumeValue: participant?.volume,
            onVolumeChange: (id, volume) => dispatch(participantVolumeChanged(id, volume))
        }));
    }, [ dispatch, participant ]);

    const forceMuteAudio = useCallback(() => {
        dispatch(
      openDialog(ForceMuteRemoteDialog, {
          participantID: participant.id
      })
        );
    }, [ dispatch, participant ]);

    const participantPin = useCallback(() => {
        dispatch(pinParticipant(participant.id));
        onLeave();

    }, [ dispatch, participant, onLeave ]);

    const handleLargeVideoFullscreen = useCallback(() => {
        dispatch(pinParticipant(participant.id, true));
        dispatch(setLargeVideoFullscreen(!largevideofullscreen));
        onLeave();
    }, [ dispatch, participant, largevideofullscreen, onLeave ]);

    // const forceMuteAudioeveryOne = useCallback(() => {
    //     dispatch(openDialog(ForceMuteEveryoneDialog, {
    //         participantID: participant.id
    //     }));
    // }, [ dispatch, participant ]);


    const onDemoteParticipant = useCallback(() => {
        const participantID = participant.id;

        dispatch(openDialog(DemoteToVisitorDialog, { participantID }));
    }, [ participant?.id ]);

    const sendPrivateMessage = useCallback(() => {
        dispatch(openChat(participant));
        dispatch(closeParticipantsPaneModal());
        dispatch(close());
        dispatch(pollsTabFocusedHandler(false));
        dispatch(setIsPollsTabFocused(false));
    }, [ dispatch, participant ]);

    const forceOriginalVoiceMode = useCallback(() => {
        dispatch(sendVoiceModeCommand(participant.id, 'original', true));
        onLeave();
    }, [ dispatch, onLeave, participant ]);

    const forceTranslatedVoiceMode = useCallback(() => {
        dispatch(sendVoiceModeCommand(participant.id, 'translated', true, {
            clonedVoiceId: cloneVoiceId,
            clonedVoiceStatus: cloneStatus
        }));
        onLeave();
    }, [ cloneStatus, cloneVoiceId, dispatch, onLeave, participant ]);

    const onBreakoutRoomButtonClick = () => {
    // DO something
    };
    const _currentRoomId = getCurrentRoomId(state1);
    const localParticipant = getLocalParticipant(state1);

    const _isModerator = Boolean(
    localParticipant?.role === PARTICIPANT_ROLE.MODERATOR
    );

    const _rooms = Object.values(rooms);
    const breakoutRoomsButtons = [];

    const getButtonProps = key => {
        return {
            key,
            participantID: participant?.id
        };
    };

    if (_isModerator) {
        _rooms.forEach(room => {
            if (room.id !== _currentRoomId) {
                breakoutRoomsButtons.push(
          <SendToRoomButton
              { ...getButtonProps(BUTTONS.SEND_PARTICIPANT_TO_ROOM) }
              key = { room.id }
              onClick = { onBreakoutRoomButtonClick }
              room = { room } />
                );
            }
        });
    }

    if (!participant) {
        return null;
    }

    return (
        <>
            <ParticipantContextMenu
                offsetTarget = { offsetTarget }
                onEnter = { onEnter }
                onLeave = { onLeave }
                onSelect = { onSelect }>
                {isLocalModerator && (
                    <ContextMenuItemGroup background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }>
                        <>
                            {!isParticipantAudioMuted && (
                                <ContextMenuItem
                                    background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                                    color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                    onClick = { muteAudio(participant.id) }>
                                    <ContextMenuIcon
                                        fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                        src = { IconMicDisabled } />
                                    <span style = { menuStyle }>{t('dialog.muteParticipantButton')}</span>
                                </ContextMenuItem>
                            )}
                            <ContextMenuItem
                                background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                                color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                onClick = { muteEveryoneElse }>
                                <ContextMenuIcon
                                    fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                    src = { IconMuteEveryoneElse } />
                                <span style = { menuStyle }>{t('toolbar.accessibilityLabel.muteEveryoneElse')}</span>
                            </ContextMenuItem>
                            <ContextMenuItem
                                background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                                color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                onClick = { muteEveryoneElseVideo }>
                                <ContextMenuIcon
                                    fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                    src = { IconMuteVideoEveryoneElse } />
                                <span style = { menuStyle }>{t('videothumbnail.domuteVideoOfOthers')}</span>
                            </ContextMenuItem>
                        </>
                    </ContextMenuItemGroup>
                )}
                <ContextMenuItemGroup background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }>
                    {isMainModerator && (
                        <ContextMenuItem
                            $disable = { _.isNil(jwt) }
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { grantModerator }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconCrown } />
                            <span style = { menuStyle }>
                                {isPersonModerator
                                    ? t('dialog.revokeModerationTitle')
                                    : t('toolbar.accessibilityLabel.grantModerator')}
                            </span>
                        </ContextMenuItem>
                    )}
                    {/* A moderator already outranks a panelist, and the server refuses to
                      * change their webinar role, so the option is not offered for them. */}
                    {canGrantRoles && !isPersonModerator && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { togglePanelist }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconMicrophone } />
                            <span style = { menuStyle }>
                                {isParticipantPanelist(participant)
                                    ? t('participantsPane.actions.revokePanelist')
                                    : t('participantsPane.actions.grantPanelist')}
                            </span>
                        </ContextMenuItem>
                    )}
                    {/* Presenter: share-only role (screen share, whiteboard, livepad,
                      * YouTube), microphone and camera locked. Same server-granted
                      * mechanism as panelist; not offered for moderators. */}
                    {canGrantRoles && !isPersonModerator && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { togglePresenter }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconShareDesktop } />
                            <span style = { menuStyle }>
                                {isParticipantPresenter(participant)
                                    ? t('participantsPane.actions.revokePresenter')
                                    : t('participantsPane.actions.grantPresenter')}
                            </span>
                        </ContextMenuItem>
                    )}
                    {/* Hide any occupant (including other moderators) from the
                      * audience. Server-verified; guests/visitors stop rendering
                      * their tile, moderators keep seeing everyone. */}
                    {canGrantRoles && (hiddenMeEnabled || participant?.webinarHidden === true) && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { toggleHidden }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconVideoOff } />
                            <span style = { menuStyle }>
                                {participant?.webinarHidden === true
                                    ? t('participantsPane.actions.showToOthers')
                                    : t('participantsPane.actions.hideFromOthers')}
                            </span>
                        </ContextMenuItem>
                    )}
                    {/* Move an occupant between the stage and backstage. Any
                      * occupant can be moved, whatever their role; Prosody
                      * verifies the moderator and issues the backstage invite
                      * before relaying the request. */}
                    {canGrantRoles && (backstageEnabled || inBackstage) && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { moveStageOccupant }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconUserGroup } />
                            <span style = { menuStyle }>
                                {inBackstage
                                    ? t('participantsPane.actions.goLive')
                                    : t('participantsPane.actions.sendToBackstage')}
                            </span>
                        </ContextMenuItem>
                    )}
                    {isLocalModerator
            && (isParticipantVideoMuted || (
                <ContextMenuItem
                    background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                    color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                    onClick = { muteVideo }>
                    <ContextMenuIcon
                        fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                        src = { IconVideoOff } />
                    <span style = { menuStyle }>{t('participantsPane.actions.stopVideo')}</span>
                </ContextMenuItem>
            ))}
                    {isLocalModerator && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { forceMuteVideo }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconVideoOff } />
                            <span style = { menuStyle }>
                                {lockvideo[`${participant?.id}`] !== undefined
                && lockvideo[`${participant?.id}`] === true
                                    ? t('participantsPane.actions.unForceMuteVideo')
                                    : t('participantsPane.actions.forceMuteVideo')}
                            </span>
                        </ContextMenuItem>
                    )}
                    {isLocalModerator && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { forceMuteAudio }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconMicDisabled } />
                            <span style = { menuStyle }>
                                {lockaudio[`${participant?.id}`] !== undefined
                && lockaudio[`${participant?.id}`] === true
                                    ? t('participantsPane.actions.unForceMute')
                                    : t('participantsPane.actions.forceMute')}
                            </span>
                        </ContextMenuItem>
                    )}
                    <ContextMenuItem
                        background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                        color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                        onClick = { volumeControl }>
                        <ContextMenuIcon
                            fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            src = { IconVolume } />
                        <span style = { menuStyle }>{t('volumeSlider.label')}</span>
                    </ContextMenuItem>
                    {_visibleButtons.has('pin-user') && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { participantPin }>
                            <ContextMenuIcon
                                src = { IconUserPinned }
                                strokeColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' } />
                            <span style = { menuStyle }>{participant?.pinned ? t('videothumbnail.unpinMe') : t('videothumbnail.pinMe')}</span>
                        </ContextMenuItem>
                    )}
                    {(window.innerWidth < 500 || isDeviceLandScape()) && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { handleLargeVideoFullscreen }>
                            <ContextMenuIcon
                                src = { IconUserPinned }
                                strokeColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' } />
                            <span style = { menuStyle }>{largevideofullscreen ? t('videothumbnail.exitLargeVideoFullscreen') : t('videothumbnail.makeLargeVideoFullscreen')}</span>
                        </ContextMenuItem>
                    )}
                    {isLocalModerator && _visibleButtons.has('moderator-pin')
                        && (!participant?.webinarHidden || participant?.moderatorPin) && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { participantModeratorPin }>
                            <ContextMenuIcon
                                src = { IconModeratorPin }
                                strokeColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' } />
                            <span style = { menuStyle }>{participant?.moderatorPin ? t('videothumbnail.unpinEveryone') : t('videothumbnail.pinEveryone')}</span>
                        </ContextMenuItem>
                    )}
                    {_visibleButtons.has('fit-video') && <ContextMenuItem
                        background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                        color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                        onClick = { handleobjectfit }>
                        <ContextMenuIcon
                            fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            src = { objectFit === 'cover' ? IconContain : IconCover } />
                        <span style = { menuStyle }>{objectFit === 'cover' ? t('tooltip.switchToContain') : t('tooltip.switchToCover')}</span>
                    </ContextMenuItem>}
                    {isLocalModerator && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { kick }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconCloseCircle } />
                            <span style = { menuStyle }>{t('videothumbnail.kick')}</span>
                        </ContextMenuItem>
                    )}
                    {visitorsSupported && _isModerator && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { onDemoteParticipant }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconUserGroup } />
                            <span style = { menuStyle }>{t('videothumbnail.demote')}</span>
                        </ContextMenuItem>
                    )}
                    {_isModerator && participant.raisedHand && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { () => clearRaisedHandByParticipant(participant) }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '#fff' }
                                src = { IconRaisedHandSlash }
                                strokeColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '#fff' } />
                            <span style = { menuStyle }>{t('participantsPane.actions.clearRaisedHand')}</span>
                        </ContextMenuItem>
                    )}
                    {isChatButtonEnabled && !visitorsMode && !isPrivateChatDisabled && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { sendPrivateMessage }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconChat } />
                            <span style = { menuStyle }>{t('toolbar.accessibilityLabel.privateMessage')}</span>
                        </ContextMenuItem>
                    )}
                    {voiceModeEnabled && <ContextMenuItem
                        background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                        color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                        disable = { true }>
                        <ContextMenuIcon
                            fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            src = { IconVolume } />
                        <span style = { menuStyle }>{t('transcribing.cloneStatusLabel', { status: cloneStatusLabel })}</span>
                    </ContextMenuItem>}
                    {voiceModeEnabled && isLocalModerator && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { forceOriginalVoiceMode }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconVolume } />
                            <span style = { menuStyle }>{t('transcribing.voiceMenuForceOriginal')}</span>
                        </ContextMenuItem>
                    )}
                    {voiceModeEnabled && isLocalModerator && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { forceTranslatedVoiceMode }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconVolume } />
                            <span style = { menuStyle }>{t('transcribing.voiceMenuForceTranslated')}</span>
                        </ContextMenuItem>
                    )}
                </ContextMenuItemGroup>
                {breakoutRoomsButtons.length > 0 && (
                    <ContextMenuItemGroup
                        background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }>
                        <div style = { _inlineStyles._s0 }>
                            {t('breakoutRooms.actions.sendToBreakoutRoom')}
                        </div>
                        {breakoutRoomsButtons}
                    </ContextMenuItemGroup>
                )}
            </ParticipantContextMenu>
        </>
    );
};
